<template>
    <Teleport to="#mellon-webecr-payment-method-vue-app">
        <div id="mellon-webecr-payment-method"
             class="mellon-webecr"
             data-component="MellonWebECRPaymentMethod">
            <!-- Payment status text -->
            <div class="payment-terminal-screen">
                <div v-if="payment"
                     class="message-line">
                    {{ l('PAYMENT.TXN_AMOUNT') }}
                    <strong>{{ currencyFilter(payment.amount) }}</strong>
                </div>

                <!-- Error message (if applicable) -->
                <ErrorMessage v-if="error"
                              :error="error"></ErrorMessage>

                <div v-if="statusMessage"
                     class="message-line mellon-webecr__status-message"
                     :class="statusTextClass">
                    {{ statusMessage }}
                    <i v-if="showLoadingSpinner"
                       class="fa-solid fa-spinner fa-spin"></i>
                </div>

                <div v-if="payment?.authCode"
                     class="message-line">
                    {{ l('PAYMENT.AUTH_CODE') }}
                    <strong>{{ payment.authCode }}</strong>
                </div>
            </div>

            <!-- In transaction buttons (row 1) - currently only shown if the txn result is unknown -->
            <div v-if="transactionInProgress && showRecheckButton"
                 class="btn-row btn-row--no-stretch mt-2">
                <!-- Check again button -->
                <SquareBtn color="warning"
                           size="lg"
                           class="mellon-webecr__recheck-txn-btn"
                           :disabled="loading"
                           @click="updateTransactionStatus">
                    <i class="fa-solid fa-arrow-rotate-right"></i>
                    {{ l('PAYMENT.RECHECK_BTN') }}
                </SquareBtn>
            </div>

            <!-- In transaction buttons (row 2) - currently only shown if the txn result is unknown -->
            <div v-if="transactionInProgress && showRecheckButton"
                 class="btn-row btn-row--no-stretch mt-2">
                <!-- Abandon payment button -->
                <SquareBtn color="default"
                           size="sm"
                           :disabled="loading"
                           class="mellon-webecr__abandon-txn-btn"
                           @click="abandon">
                    <i class="fa-solid fa-trash-xmark"></i>
                    {{ l('MELLON_WEBECR.ABANDON_TXN_BTN') }}
                </SquareBtn>
            </div>

            <!-- Not in transaction buttons (row 1) -->
            <div v-if="!transactionInProgress && cartRemainingBalance"
                 class="btn-row btn-row--no-stretch mt-2">
                <!-- Start button -->
                <SquareBtn v-if="cartRemainingBalance"
                           color="primary"
                           size="lg"
                           class="mellon-webecr__start-txn-btn"
                           @click="initiateTransactionAndHandleErrors(cartRemainingBalance)">
                    <i class="fa-solid fa-credit-card"></i>
                    {{ l('PAYMENT.START_CARD_TRANSACTION_BTN', { amount: cartRemainingBalance }) }}
                </SquareBtn>
            </div>

            <!-- Not in transaction buttons (row 2) -->
            <div v-if="!transactionInProgress && cartRemainingBalance"
                 class="btn-row btn-row--no-stretch">
                <!-- Partial payment button -->
                <SquareBtn color="default"
                           :disabled="transactionInProgress"
                           @click="makePartialPayment">
                    <i class="fa-solid fa-input-numeric"></i>
                    {{ l('PAYMENT.PARTIAL_PAYMENT_BTN') }}
                </SquareBtn>
            </div>

            <!-- Debugging info -->
            <div class="mellon-webecr__debug">
                <span>
                    External ID:
                    <strong class="user-selectable">{{ payment?.thirdPartyID }}</strong>
                </span>
                <span>
                    Intent ID:
                    <strong class="user-selectable">{{ transactionIntent?.Id }}</strong>
                </span>
                <span>
                    Intent Status:
                    <strong class="user-selectable">{{ transactionIntent?.Status }}</strong>
                </span>
                <span>
                    Intent Result:
                    <strong class="user-selectable">{{ transactionIntent?.Result }}</strong>
                </span>
                <span>
                    Transaction ID:
                    <strong class="user-selectable">{{ transactionIntent?.Transaction }}</strong>
                </span>

                <!-- In dev mode show the abort button -->
                <!--                <SquareBtn v-if="isDev && transactionInProgress"-->
                <!--                           :disabled="!transactionRequestSent"-->
                <!--                           color="danger"-->
                <!--                           size="xs"-->
                <!--                           class="mellon-webecr__abort-txn-btn"-->
                <!--                           @click="abort">-->
                <!--                    <i class="fa-solid fa-ban"></i>-->
                <!--                    {{ l('CANCEL') }}-->
                <!--                </SquareBtn>-->
            </div>
        </div>
    </Teleport>
</template>

<script>
import ErrorMessage from '@/vue/components/Errors/ErrorMessage';
import EventNames from '@/lib/Events/EventNames';
import logFactory from '@/libraries/DigiTickets/Logging/logFactory';
import MellonWebECRIntentResult from '@/lib/PaymentMethods/MellonWebECR/Enums/MellonWebECRIntentResult';
import MellonWebECRIntentStatus from '@/lib/PaymentMethods/MellonWebECR/Enums/MellonWebECRIntentStatus';
import PaymentMethodRef from '@/lib/PaymentMethods/PaymentMethodRef';
import RequestAbortedError from '@/lib/Errors/RequestAbortedError';
import SquareBtn from '@/vue/components/SquareBtn';
import TransactionInProgress from '@/lib/Payments/TransactionInProgress';
import { container } from '@/container';
import { getCardTypeFromCardNumber } from '@/lib/Payments';
import { l } from '@/vue/filters/langFilter';
import { mapState } from 'vuex';
import { MODAL_NAMES__EPOS } from '@/epos/vue/modals/MODAL_NAMES__EPOS';
import { suppressModalCancelledErrorOrReThrow } from '@/lib/Errors';
import { timeoutPromise } from '@/functions/functions';

const log = logFactory('MellonWebECRPaymentMethod');

export default {
    components: {
        ErrorMessage,
        SquareBtn,
    },

    data() {
        return {
            /**
             * We can't use a computed prop for this because it doesn't play nice with the non-Vue code.
             * So this property is updated by the updateCartRemainingBalance method.
             */
            cartRemainingBalance: null,

            /**
             * @type {MellonWebECRDeviceConfig|null}
             */
            deviceConfig: null,

            /**
             * @type {Error|null}
             */
            error: null,

            /**
             * @type {Order}
             */
            order: null,

            /**
             * @type {MellonWebECRPayment|null}
             */
            payment: null,

            /**
             * @type {PaymentMethod|null}
             */
            paymentMethod: null,

            /**
             * @type {string|null}
             */
            statusMessage: null,

            /**
             * @type {string|null}
             */
            statusMessageType: null,

            /**
             * @type {?TransactionInProgress}
             */
            transactionInProgress: null,

            /**
             * @type {boolean}
             */
            transactionRequestSent: false,

            /**
             * @type {?MellonWebECRTransaction}
             */
            transaction: null,

            /**
             * @type {?MellonWebECRTransactionIntent}
             */
            transactionIntent: null,

            /**
             * @type {boolean}
             */
            loading: false,

            /**
             * How long (in ms) to display a message that the transaction is being abandoned.
             *
             * @type {number}
             */
            abandonTransactionDisplayTimeout: 1000,
        };
    },

    computed: {
        ...mapState(['isDev']),

        /**
         * @returns {boolean}
         */
        showLoadingSpinner() {
            return this.loading
                || (this.transactionInProgress && !this.transactionIntentResultIsUnknown);
        },

        /**
         * @returns {boolean}
         */
        showRecheckButton() {
            return this.transactionIntent?.Status === MellonWebECRIntentStatus.SENT
                || this.transactionIntent?.Status === MellonWebECRIntentStatus.PENDING
                || this.transactionIntentResultIsUnknown;
        },

        /**
         * If the initial request to start a Mellon transaction times out after the 3 minutes
         * we wait, it will respond with a result of UNKNOWN.
         * At this point we should display options to the user to check again or give up.
         *
         * Note that "unknown" is a specific response, and not a general state of not-knowingness.
         *
         * @returns {boolean}
         */
        transactionIntentResultIsUnknown() {
            return this.transactionIntent?.Result === MellonWebECRIntentResult.UNKNOWN;
        },

        statusTextClass() {
            switch (this.statusMessageType) {
                case 'success':
                    return 'message-line-success';
                case 'warning':
                    return 'message-line-warning';
                case 'danger':
                    return 'message-line-danger';
                default:
                    return 'message-line-info';
            }
        },
    },

    async created() {
        // The MellonWebECR driver is initialized in EposIndexCtrl when the app starts up.
        // So we should be ready to roll at this point.

        this.paymentMethod = container.mellonWebECRDriver.getPaymentMethod();

        // Load the terminal settings for the device.
        this.deviceConfig = container.currentDevice.device.getPaymentTerminalConfigForMethod(
            PaymentMethodRef.CARD_MELLON_WEBECR,
        );

        // We need to load the order early on because we need to know the bookingRef even for payment recovery.
        try {
            this.order = await this.loadOrder();
        } catch (e) {
            this.error = e;
            return;
        }

        const txnInProgress = container.cartService.getTransactionInProgress();
        if (txnInProgress && txnInProgress.paymentMethodRef === this.paymentMethod.ref) {
            console.log('Found existing transaction in progress', txnInProgress);

            await this.showRecoveryModal(txnInProgress);

            await this.reset();
        } else {
            await this.reset();

            // Auto start the transaction if the device is configured to do so.
            const currentDevice = container.currentDevice.device;
            if (currentDevice.autoStartCardTransactions) {
                this.initiateTransactionAndHandleErrors(this.cartRemainingBalance);
            }
        }

        // When a payment is removed from the cart, which could have been voiding a Mellon payment, clean up
        // so that we're not displaying any stale data.
        this.deregisterPaymentRemovedListener = container.$rootScope.$on(
            EventNames.CART_PAYMENT_REMOVED,
            () => {
                if (!this.transactionInProgress) {
                    this.reset();
                }
            }
        );
    },

    beforeUnmount() {
        if (this.deregisterPaymentRemovedListener) {
            this.deregisterPaymentRemovedListener();
        }

        // Don't do any other cleanup because we don't want to accidentally clear data about a transaction
        // that is being abandoned and will need to be recovered.
    },

    methods: {
        async loadOrder() {
            const currentCart = container.cartService.currentCart;

            if (currentCart.isEditingOrder) {
                return currentCart.originalOrder;
            }

            return this.loadCurrentCartUnconfirmedOrder();
        },

        /**
         * TODO: This doesn't need to be specific to Mellon.
         * It could be moved elsewhere and used by any payment method that needs it.
         *
         * @returns {Promise<Order>}
         */
        async loadCurrentCartUnconfirmedOrder() {
            this.setStatusMessage('MELLON_WEBECR_STATUS.LOADING_ORDER');
            const order = await container.cartService.loadUnconfirmedOrder();
            this.setStatusMessage();

            return order;
        },

        /**
         * Start a card transaction for the requested amount.
         * Catches errors and sets the error state.
         *
         * @param {number} amountInMajorUnits
         *
         * @returns {Promise}
         */
        async initiateTransactionAndHandleErrors(amountInMajorUnits) {
            try {
                await this.initiateTransaction(amountInMajorUnits);
            } catch (error) {
                log.error('Error initiating transaction.', error);

                if (error instanceof RequestAbortedError) {
                    error.message = l('MELLON_WEBECR_STATUS.ABORTED');
                }

                this.error = error;
                this.setStatusMessage();
                this.onTransactionCompleted(true);
            }
        },

        /**
         * Start a card transaction for the requested amount.
         * Throws on error - you probably want to call initiateTransactionAndHandleErrors instead of
         * directly calling this.
         *
         * @param {number} amountInMajorUnits
         *
         * @returns {Promise}
         * @throws {Error}
         */
        async initiateTransaction(amountInMajorUnits) {
            log.log('Starting transaction with amount', amountInMajorUnits);

            await this.reset();

            // Create a transaction in progress. To keep track of this transaction.
            // More data will be added to this as it progresses.
            // We only keep this within the controller and don't save it on the cart until the point at which
            // we would need to recover once it is starting. We can create a payment and sign it without any
            // commitments. Only once it has been sent to the terminal are we committed.
            // The reason for setting it in the controller already is that we also use the presence of this object
            // to display that we are in a transaction to the user.
            await this.setTransactionInProgress(
                new TransactionInProgress(
                    this.paymentMethod.ref,
                    null,
                    amountInMajorUnits,
                    {
                        status: 'starting',
                    },
                ),
                false, // Don't save it to the cart yet.
            );
            container.paymentState.setPaymentModalCancelable(false, this.paymentMethod.ref);

            const currentCart = container.cartService.currentCart;

            this.payment = null;

            // MellonWebECR payments need and additional step than all other card payments.
            // We need to send a request to an API to create a payment signature before
            // we can use the payment terminal.
            // This signature contains some information about the tax in the order.

            // For new orders we will have created an unconfirmed order.
            // For adjustment orders we don't have the adjustment created yet. So we'll have to make do with
            // using the original order.

            /**
             * @type {TaxSummary}
             */
            let taxSummary;

            if (currentCart.isEditingOrder) {
                // This is an adjustment order.
                // Attica should only be using adjustments to completely cancel an order.
                // Therefore, we can calculate the tax etc. based on a full refund of the original order.

                if (!this.order.taxSummary) {
                    this.setStatusMessage('MELLON_WEBECR_STATUS.LOADING_TAX');
                    await container.taxManager.addOrderTaxToOrder(this.order);
                }
                taxSummary = this.order.taxSummary;
            } else {
                // This is a new order and an unconfirmed order should have been created already.

                // Load the tax for the unconfirmed order if we don't already have it.
                if (!currentCart.unconfirmedOrder.taxSummary) {
                    this.setStatusMessage('MELLON_WEBECR_STATUS.LOADING_TAX');
                    await container.cartService.modifyCurrentCart(async (cart) => {
                        await container.taxManager.addOrderTaxToOrder(cart.unconfirmedOrder);
                    });
                }
                taxSummary = currentCart.unconfirmedOrder.taxSummary;
            }

            // Create an empty Payment to store the payment info.
            this.setStatusMessage('MELLON_WEBECR_STATUS.CREATING_PAYMENT');
            this.payment = await container.mellonWebECRDriver.createNewPayment(
                this.order,
                amountInMajorUnits,
            );

            // Save the payment with the transaction in progress.
            this.transactionInProgress.txnRef = this.payment.thirdPartyID;
            this.transactionInProgress.setValue('payment', this.payment);
            this.transactionInProgress.setValue('status', 'signing');
            await this.setTransactionInProgress(
                this.transactionInProgress,
                false, // Still don't save to the cart yet.
            );

            // Sent the payment info to Entersoft to get a signature.
            this.setStatusMessage('MELLON_WEBECR_STATUS.SIGNING_PAYMENT');

            // We need to send an "externalID" to Entersoft to sign the payment.
            // This "externalID" must exactly match the "supplierUID" that is used later on the invoice.
            // On the invoice it will be the order's thirdPartyRef, or {thirdPartyRef}-credit for refunds.

            // TODO: Once payment has been taken we can't allow cancelling the order anymore because that
            // will break things. If, for example, you create and order with thirdPartyRef ABCD,
            // and sign a payment for it, that payment will use externalID ABCD.
            // If you go back to the cart then come back to pay again a new unconfirmed order will have been
            // created, with thirdPartyRef ABCD.1. Now you'll have a payment on that order with thirdPartyRef
            // ABC and an order with thirdPartyRef ABCD.1, not matching.
            // We could handle this by stripping the .1 from the thirdPartyRef when signing the payment,
            // or when creating the invoice.

            await container.mellonWebECRDriver.signPayment(
                this.order,
                this.payment,
                this.deviceConfig.terminalTerminalID,
                taxSummary,
            );

            // Save the payment with the transaction in progress now that it has been signed.
            this.transactionInProgress.setValue('payment', this.payment);
            this.transactionInProgress.setValue('status', 'sending');
            await this.setTransactionInProgress(
                this.transactionInProgress,
                true, // Now commit it to the cart.
            );

            this.transactionRequestSent = true;
            const transactionIntent = await this.sendTransactionRequest(this.payment);

            // There are two pieces of data, a transaction intent and a transaction.
            // The response of the send transaction request will be a transaction intent.

            // If the transaction is still in progress, the intent's Transaction field will be null.
            // If the transaction is complete, the intent's Transaction field will be...
            // - if came from the start transaction endpoint, it will be an object of the transaction details.
            // - if it came from the get intent endpoint, it will just be the transaction ID,
            //   and you need to make a separate request to get the transaction details.

            // Bust out the transaction into its own variable, and replace the intent's Transaction
            // with the ID for consistency.
            let transaction = null;
            if (transactionIntent.Transaction && typeof transactionIntent.Transaction !== 'string') {
                transaction = transactionIntent.Transaction;
                transactionIntent.Transaction = transaction.Id;
            }

            // If the transaction is not yet complete, we can poll the getTransactionIntent endpoint to get the
            // latest status of the intent and fetch the details of the transaction itself when those are available.
            // This function handles all this:
            this.setTransactionStatus(transactionIntent, transaction);
        },

        /**
         * @private
         *
         * @param {MellonWebECRPayment} payment
         *
         * @returns {Promise<MellonWebECRTransactionIntent>}
         */
        async sendTransactionRequest(payment) {
            log.debug('Starting transaction', payment);
            this.setStatusMessage('MELLON_WEBECR_STATUS.STARTING_TXN');

            /**
             * @type {MellonWebECRStartTransactionRequest}
             */
            const transactionRequest = container.mellonWebECRDriver.createTransactionRequest(
                payment,
            );
            log.debug('Transaction request', transactionRequest);

            return container.mellonWebECRDriver.client.startTransaction(
                this.deviceConfig.terminalID,
                transactionRequest,
            );
        },

        async updateTransactionStatus() {
            this.setStatusMessage('MELLON_WEBECR_STATUS.CHECKING_TXN');

            // Fetch the latest version of the transaction intent and transaction from Mellon.
            const {
                transactionIntent,
                transaction,
            } = await this.fetchTransactionStatus();

            // Update the displayed state and act on the transaction status.
            this.setTransactionStatus(transactionIntent, transaction);
        },

        async abandon() {
            const confirmed = await container.dialogService.confirm(
                'MELLON_WEBECR.CONFIRM_ABANDON_TXN',
                {
                    okColor: 'danger',
                    okLabel: 'MELLON_WEBECR.CONFIRM_ABANDON_CONFIRM_BTN',
                    cancelColor: 'default',
                    cancelLabel: 'MELLON_WEBECR.CONFIRM_ABANDON_CANCEL_BTN',
                }
            );

            if (!confirmed) {
                return;
            }

            // Display something to user to make it look like something is happening.
            this.loading = true;

            this.setStatusMessage('MELLON_WEBECR_STATUS.ABANDONING_TXN');

            await timeoutPromise(this.abandonTransactionDisplayTimeout);

            // We don't need to do anything special. Just forget about the transaction.
            this.reset();
        },

        /**
         * Call the Mellon API to fetch the latest version of the transaction intent.
         * If the intent includes a transaction ID, fetch the transaction as well.
         *
         * @returns {Promise<{transactionIntent: ?MellonWebECRTransactionIntent, transaction: ?MellonWebECRTransaction}>}
         */
        async fetchTransactionStatus() {
            const response = {
                transactionIntent: null,
                transaction: null,
            };

            if (!this.transactionIntent) {
                return response;
            }

            this.loading = true;

            try {
                response.transactionIntent = await container.mellonWebECRDriver.client.getTransactionIntent(
                    this.transactionIntent.Id,
                );

                if (response.transactionIntent?.Transaction) {
                    response.transaction = await container.mellonWebECRDriver.client.getTransaction(
                        response.transactionIntent.Transaction,
                    );
                }
            } finally {
                this.loading = false;
            }

            return response;
        },

        /**
         * Update the displayed state based on the latest version of the transaction intent and transaction.
         * If the transaction is complete, add the payment to the cart.
         * If the transaction is still in progress, set a timeout to check again in 5 seconds.
         * If the transaction has failed, display an error message.
         *
         * @param {MellonWebECRTransactionIntent} transactionIntent
         * @param {?MellonWebECRTransaction} [transaction]
         */
        setTransactionStatus(transactionIntent, transaction) {
            this.transactionInProgress.setValue('transactionIntent', transactionIntent);
            this.transactionInProgress.setValue('transaction', transaction);
            this.transactionInProgress.setValue('status', 'sent');
            this.setTransactionInProgress(this.transactionInProgress);

            log.debug('transactionIntent', transactionIntent);
            log.debug('transaction', transaction);

            this.transactionIntent = transactionIntent;
            this.transaction = transaction;

            // If we have a transaction already, not just an intent, check the status of that.
            // We should only have a transaction if the intent is complete and is approved.
            if (transaction) {
                if (transaction.Approved) {
                    this.onTransactionApproved(
                        transactionIntent,
                        transaction,
                        this.payment,
                    );
                    this.onTransactionCompleted();
                    return;
                }
            }

            switch (transactionIntent.Status) {
                case MellonWebECRIntentStatus.PENDING:
                    // PENDING
                    // Intent has been registered to the backend and is pending to be sent to the device
                    this.setStatusMessage('MELLON_WEBECR_STATUS.INTENT_STATUS__PENDING');
                    break;
                case MellonWebECRIntentStatus.SENT:
                    // SENT
                    // Intent has been sent to the device
                    this.setStatusMessage('MELLON_WEBECR_STATUS.INTENT_STATUS__SENT');
                    break;
                case MellonWebECRIntentStatus.COMPLETED:
                    // COMPLETED
                    // Intent has been successfully completed by the device and has registered the results
                    //
                    // Note: this does not necessarily mean that it was successful, just that it is over.
                    this.setStatusMessage('MELLON_WEBECR_STATUS.INTENT_STATUS__COMPLETED');
                    break;
            }

            // The intent itself can tell us some information about the transaction.
            if (transactionIntent.Result) {
                switch (transactionIntent.Result) {
                    case MellonWebECRIntentResult.APPROVED:
                        // APPROVED
                        // The transaction has been completed and approved by the authorization system
                        //
                        // We should never really get here, because if the transaction intent says it's approved
                        // it should have included a transaction ID, which should have also been loaded, and
                        // should have been checked above.
                        log.warn(
                            'Transaction intent says it is approved, but the transaction is not complete.',
                            transactionIntent,
                            transaction,
                        );
                        // If we just do nothing at this point we can let another check happen shortly,
                        // which will hopefully include the actual transaction.
                        this.setStatusMessage('MELLON_WEBECR_STATUS.INTENT_RESULT__APPROVED', 'success');
                        break;

                    case MellonWebECRIntentResult.DECLINED:
                        // DECLINED
                        // The transaction has been completed and declined by the authorization system
                        this.setStatusMessage('MELLON_WEBECR_STATUS.INTENT_RESULT__DECLINED', 'danger');
                        this.onTransactionCompleted();
                        return;

                    case MellonWebECRIntentResult.CANCELLED:
                        // CANCELLED
                        // The transaction has been cancelled by the POS user before reaching completion
                        this.setStatusMessage('MELLON_WEBECR_STATUS.INTENT_RESULT__CANCELLED', 'danger');
                        this.onTransactionCompleted();
                        return;

                    case MellonWebECRIntentResult.FAILED:
                        // FAILED
                        // The transaction has failed to complete
                        this.setStatusMessage('MELLON_WEBECR_STATUS.INTENT_RESULT__FAILED', 'danger');
                        this.onTransactionCompleted();
                        return;

                    case MellonWebECRIntentResult.UNKNOWN:
                        // UNKNOWN
                        // The transaction result is unknown. Only possible if the device hasn't responded with results
                        // after the initial create transaction request.
                        this.setStatusMessage(
                            'MELLON_WEBECR_STATUS.INTENT_RESULT__UNKNOWN',
                            'warning',
                        );
                        break;

                    case MellonWebECRIntentResult.BUSY:
                        // BUSY
                        // The transaction has failed because the POS is currently unavailable for transactions (either
                        // processing another transaction or under maintenance)
                        this.setStatusMessage(
                            'MELLON_WEBECR_STATUS.INTENT_RESULT__BUSY',
                            'danger',
                        );
                        this.onTransactionCompleted();
                        return;

                    case MellonWebECRIntentResult.MAX_TRANSACTIONS:
                        // MAX_TRANSACTIONS
                        // The POS device has reached its transaction limit for the specific batch. Batch
                        // closing should be performed on the device before continuing transactions
                        this.setStatusMessage(
                            'MELLON_WEBECR_STATUS.INTENT_RESULT__MAX_TRANSACTIONS',
                            'danger',
                        );
                        this.onTransactionCompleted();
                }
            }
        },

        /**
         * @param {MellonWebECRTransactionIntent} transactionIntent
         * @param {MellonWebECRTransaction} transaction
         * @param {MellonWebECRPayment} payment
         */
        onTransactionApproved(transactionIntent, transaction, payment) {
            // Transaction is complete.
            this.setStatusMessage('MELLON_WEBECR_STATUS.INTENT_RESULT__APPROVED', 'success');

            // Add some details to the payment and then add it to the cart.
            payment.authCode = transaction.AuthCode;
            payment.lastDigits = transaction.CardPAN?.slice(-4);

            if (transaction.CardPAN) {
                // Determine the card type from the card number.
                const cardTypeRef = getCardTypeFromCardNumber(transaction.CardPAN);
                if (cardTypeRef) {
                    payment.setCardTypeRef(cardTypeRef);
                }
            }

            // Store the transaction ID as the transactionRef of the payment.
            payment.transactionRef = transaction.Id;

            // The transaction intent ID can also be useful to keep.
            // So add that in the statusDetail.
            payment.addToJsonStatusDetail('terminalID', transactionIntent.Terminal);
            payment.addToJsonStatusDetail('transactionID', transaction.Id);
            payment.addToJsonStatusDetail('transactionIntentID', transactionIntent.Id);

            log.log('Payment approved', payment);

            // We're done. Add the payment to the cart.
            this.addPaymentToCart(payment);

            container.paymentState.setSuccessMessage(l('MELLON_WEBECR.SUCCESS_MESSAGE') + payment.authCode);
        },

        /**
         * @param {Payment} payment
         */
        addPaymentToCart(payment) {
            container.eventBus.broadcast(EventNames.ADD_PAYMENT_TO_CART_REQUESTED, payment);
        },

        /**
         * Clean up after a transaction has finished (successfully or not).
         * Without clearing the status of the transaction, so we can see what happened.
         */
        async onTransactionCompleted() {
            // Update the remaining balance displayed.
            this.cartRemainingBalance = container.cartService.getRemainingBalance();

            // Clear the transaction in progress flag.
            await this.setTransactionInProgress(null);

            // Clear the payment cancelable flag.
            // Little hack to fix Angular not seeing changes - set it then clear it.
            container.paymentState.setPaymentModalCancelable(true, this.paymentMethod.ref);
            await timeoutPromise(1);
            container.paymentState.clearPaymentModalCancelable();

            this.transactionRequestSent = false;

            // Note we don't clear any error or status message here so it remains shown to the user.
        },

        /**
         * This resets everything to a clean state, ready to start a new transaction.
         */
        async reset() {
            this.error = null;
            this.payment = null;
            this.transactionIntent = null;
            this.transaction = null;
            this.loading = false;
            this.setStatusMessage(null);

            await this.onTransactionCompleted();
        },

        /**
         * @param {string|null} langKey
         * @param {"success"|"warning"|"danger"|null} [type]
         */
        setStatusMessage(langKey, type) {
            this.statusMessage = langKey ? container.langService.getText(langKey) : null;
            this.statusMessageType = langKey ? (type || 'info') : null;
        },

        makePartialPayment() {
            container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.PARTIAL_PAYMENT,
                    {
                        mode: 'make',
                        isRefund: container.cartService.isRefund(),
                        paymentMethod: container.cartService.getSelectedPaymentMethod(),
                        orderTotal: container.cartService.getTotalExcludingOriginalOrder(),
                        remainingBalance: container.cartService.getRemainingBalance(),
                        maxPayment: container.cartService.getRemainingMaxPayment(),
                    },
                )
                .then((result) => {
                    this.initiateTransactionAndHandleErrors(result.value);
                })
                .catch(suppressModalCancelledErrorOrReThrow);
        },

        abort() {
            // Cancel any in-progress API requests.
            container.mellonWebECRDriver.client.abortRequests();

            // We don't set a status message here. Aborting will cause the client to error
            // and that error will be handled by the initiateTransactionAndHandleErrors method.
        },

        async setTransactionInProgress(transactionInProgress, saveToCart = true) {
            this.transactionInProgress = transactionInProgress;

            if (!saveToCart) {
                // Don't save the transaction in progress to the cart.
                return;
            }

            if (transactionInProgress) {
                // Save the transaction in progress to the cart.
                await container.cartService.setTransactionInProgress(this.transactionInProgress);
            } else {
                // Clear the transaction in progress from the cart.
                await container.cartService.clearTransactionInProgress();
            }
        },

        /**
         * @param {TransactionInProgress} transactionInProgress
         *
         * @returns {Promise}
         */
        async showRecoveryModal(transactionInProgress) {
            return container.vueModalFactory
                .open(
                    MODAL_NAMES__EPOS.MELLON_WEBECR_RECOVERY,
                    {
                        order: this.order,
                        transactionInProgress,
                    },
                )
                .then(
                    /**
                     * @param {MellonWebECRRecoveryResult} result
                     */
                    (result) => {
                        if (result.abandon) {
                            log.log('User has chosen to abandon the transaction', result);
                            // The user has chosen to abandon the transaction.
                            this.reset();
                        } else if (result.payment && result.transactionIntent && result.transaction) {
                            log.log('User has chosen to recover the transaction', result);
                            // The user has chosen to recover the transaction.
                            this.setTransactionInProgress(transactionInProgress);
                            this.payment = result.payment;
                            this.setTransactionStatus(
                                result.transactionIntent,
                                result.transaction,
                            );
                        }
                    },
                );
        },
    },
};
</script>

<style lang="less">
@import '../../../../../../less/variables.less';

.mellon-webecr {
    .payment-terminal-screen {
        margin: @gutter-width auto;

        &:empty {
            display: none;
        }
    }

    &__debug {
        display: flex;
        grid-gap: @gutter-width;
        justify-content: center;
        align-items: center;

        font-size: @font-size-small;
        opacity: .3;
    }
}
</style>
